import { providers, utils } from 'ethers';
import { getChainNameFromEnv, supportedChainNames } from './supportedChains';
// from https://github.com/authereum/eth-revert-reason/blob/master/index.js
// stripped out all the non-alchemy & kovan edge cases

function normalizeInput(
    network: string,
    blockNumber?: number | string
): { network: string; blockNumber: number | string } {
    return {
        network: network.toLowerCase(),
        blockNumber: blockNumber || 'latest',
    };
}

async function validateInputPreProvider(txHash: string, network: string): Promise<void> {
    // Only accept a valid txHash
    if (!/^0x([A-Fa-f0-9]{64})$/.test(txHash) || txHash.substring(0, 2) !== '0x') {
        throw new Error('Invalid transaction hash');
    }
    const networks = supportedChainNames();
    if (!networks.includes(network)) {
        throw new Error(`Not a valid network: ${network}`);
    }
}

async function validateInputPostProvider(
    blockNumber: number | string,
    provider: providers.BaseProvider
): Promise<void> {
    // Validate the block number
    if (blockNumber !== 'latest') {
        const currentBlockNumber = await provider.getBlockNumber();
        blockNumber = Number(blockNumber);

        if (blockNumber > currentBlockNumber) {
            throw new Error('You cannot use a blocknumber that has not yet happened.');
        }
    }
}

function decodeMessage(code: string): string {
    // NOTE: `code` may end with 0's which will return a text string with empty whitespace characters
    // This will truncate all 0s and set up the hex string as expected
    let codeString = `0x${code.substr(138)}`.replace(/0+$/, '');

    // If the codeString is an odd number of characters, add a trailing 0
    if (codeString.length % 2 === 1) {
        codeString += '0';
    }

    return utils.toUtf8String(codeString);
}

/**
 * Get the revert reason from just a transaction hash
 * @param {string} txHash - Hash of an Ethereum transaction
 * @param {string} network - Ethereum network name
 * @param {number} blockNumber - A block number to make the call from
 */
export async function getRevertReason(
    provider: providers.JsonRpcProvider,
    txHash: string,
    blockNumber?: number | string
): Promise<string> {
    let network = getChainNameFromEnv();
    ({ network, blockNumber } = normalizeInput(network, blockNumber));
    await validateInputPreProvider(txHash, network);
    await validateInputPostProvider(blockNumber, provider);

    const tx = (await provider.getTransaction(txHash)) as providers.TransactionRequest;
    const code = await provider.call(tx, blockNumber);
    return decodeMessage(code);
}
